'use client'

import {
  ArrowDownRightIcon,
  EllipsisHorizontalIcon,
  GiftIcon,
  LightBulbIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import { Slot } from '@radix-ui/react-slot'
import type {
  GetPools,
  PoolChainId,
  Pools,
} from '@sushiswap/graph-client/data-api'
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Chip,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Loader,
  SkeletonText,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sushiswap/ui'
import type {
  ColumnDef,
  Row,
  SortingState,
  TableState,
} from '@tanstack/react-table'
import Link from 'next/link'
import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { usePoolFilters } from 'src/app/(networks)/_ui/pools-filters-provider'
import { usePoolsInfinite } from 'src/lib/hooks'
import {
  type EvmChainId,
  EvmNative,
  SushiSwapProtocol,
  isMerklChainId,
} from 'sushi/evm'
import {
  APR_WITH_REWARDS_COLUMN,
  EXPLORE_NAME_COLUMN_POOL,
  TRANSACTIONS_1D_COLUMN,
  TVL_COLUMN,
  VOLUME_1D_COLUMN,
  VOLUME_1W_COLUMN,
} from './columns'

// Maps a sortable column's `id` to the `order_by` value the Katana API
// accepts. Columns whose id isn't listed here cannot be sorted server-side.
const COLUMN_ID_TO_ORDER_BY: Record<string, GetPools['orderBy']> = {
  liquidityUSD: 'liquidityUSD',
  volumeUSD1d: 'volumeUSD',
  txCount1d: 'txCount',
  totalApr1d: 'totalApr1d',
}

const COLUMNS = [
  EXPLORE_NAME_COLUMN_POOL,
  TVL_COLUMN,
  VOLUME_1D_COLUMN,
  VOLUME_1W_COLUMN,
  TRANSACTIONS_1D_COLUMN,
  APR_WITH_REWARDS_COLUMN,
  {
    id: 'actions',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button icon={EllipsisHorizontalIcon} variant="ghost" size="sm">
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-fit">
          <DropdownMenuLabel>
            {row.original.name}
            <Chip variant="blue" className="ml-2">
              SushiSwap V3
            </Chip>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link
                onClick={(e) => e.stopPropagation()}
                shallow={true}
                className="flex items-center"
                href={`/pool/v3/${row.original.address}`}
              >
                <ArrowDownRightIcon width={16} height={16} className="mr-2" />
                Pool details
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                onClick={(e) => e.stopPropagation()}
                shallow={true}
                className="flex items-center"
                href={`/pool/v3/${row.original.address}/create`}
              >
                <PlusIcon width={16} height={16} className="mr-2" />
                Create position
              </Link>
            </DropdownMenuItem>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild={row.original.isSmartPool}>
                  <DropdownMenuItem
                    asChild
                    disabled={!row.original.isSmartPool}
                  >
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      shallow={true}
                      className="flex items-center"
                      href={`/pool/v3/${row.original.address}/smart`}
                    >
                      <span className="relative">
                        <LightBulbIcon
                          width={16}
                          height={16}
                          className="mr-2"
                        />
                        <sup className="rounded-full bg-background absolute right-[3px]">
                          <PlusIcon width={12} height={12} />
                        </sup>
                      </span>
                      Create smart position
                    </Link>
                  </DropdownMenuItem>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-[240px]">
                  <p>
                    {!row.original.isSmartPool
                      ? 'No Liquidity Vaults available for this pool'
                      : `Liquidity Vaults optimize liquidity allocation within custom price ranges, enhancing trading efficiency by
          providing deeper liquidity around the current price, increasing Liquidity Providers (LP) fee earnings.`}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild={isMerklChainId(row.original.chainId)}>
                  <DropdownMenuItem
                    asChild
                    disabled={!isMerklChainId(row.original.chainId)}
                  >
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      shallow={true}
                      className="flex items-center"
                      href={`/pool/incentivize?fromCurrency=${
                        row.original.token0Address ===
                        EvmNative.fromChainId(
                          row.original.chainId as EvmChainId,
                        ).wrap().address
                          ? 'NATIVE'
                          : row.original.token0Address
                      }&toCurrency=${
                        row.original.token1Address ===
                        EvmNative.fromChainId(
                          row.original.chainId as EvmChainId,
                        ).wrap().address
                          ? 'NATIVE'
                          : row.original.token1Address
                      }&feeAmount=${row.original.swapFee * 10_000 * 100}`}
                    >
                      <GiftIcon width={16} height={16} className="mr-2" />
                      Add incentive
                    </Link>
                  </DropdownMenuItem>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-[240px]">
                  <p>
                    {!isMerklChainId(row.original.chainId)
                      ? 'Not available on this network'
                      : 'Add rewards to a pool to incentivize liquidity providers joining in.'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
    size: 80,
    meta: {
      disableLink: true,
      body: {
        skeleton: <SkeletonText fontSize="lg" />,
      },
    },
  } satisfies ColumnDef<Pools[number], unknown>,
] as ColumnDef<Pools[number], unknown>[]

interface PoolsTableProps {
  chainId: PoolChainId
  onRowClick?(row: Pools[number]): void
  forcedTokenSymbols?: string[]
}

export const PoolsTable: FC<PoolsTableProps> = ({
  chainId,
  onRowClick,
  forcedTokenSymbols,
}) => {
  const { tokenSymbols, protocols, farmsOnly, smartPoolsOnly } =
    usePoolFilters()

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'liquidityUSD', desc: true },
  ])

  const args = useMemo<Omit<GetPools, 'page'>>(() => {
    const tokenSymbolsSet = new Set([
      ...tokenSymbols.map((symbol) => symbol.toLowerCase()),
      ...(forcedTokenSymbols ?? []).map((symbol) => symbol.toLowerCase()),
    ])
    return {
      chainId,
      search: Array.from(tokenSymbolsSet),
      onlyIncentivized: farmsOnly,
      onlySmartPools: smartPoolsOnly,
      protocols,
      orderBy: sorting[0] ? COLUMN_ID_TO_ORDER_BY[sorting[0].id] : undefined,
      orderDirection: sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : 'desc',
    }
  }, [
    chainId,
    tokenSymbols,
    forcedTokenSymbols,
    farmsOnly,
    smartPoolsOnly,
    sorting,
    protocols,
  ])

  const { data: pools, isLoading, fetchNextPage } = usePoolsInfinite(args)

  const [data, count, totalCount] = useMemo(
    () => [
      pools?.pages?.flatMap(({ data }) => data) ?? [],
      pools?.pages?.[pools.pages.length - 1]?.count,
      pools?.pages?.[0]?.totalCount,
    ],
    [pools],
  )

  const state: Partial<TableState> = useMemo(() => {
    return {
      sorting,
      pagination: {
        pageIndex: 0,
        pageSize: data?.length,
      },
    }
  }, [data?.length, sorting])

  const rowRenderer = useCallback(
    (row: Row<Pools[number]>, rowNode: ReactNode) => {
      if (onRowClick)
        return (
          <Slot
            className="cursor-pointer"
            onClick={() => onRowClick?.(row.original)}
          >
            {rowNode}
          </Slot>
        )
      return rowNode
    },
    [onRowClick],
  )

  return (
    <InfiniteScroll
      dataLength={data.length}
      next={fetchNextPage}
      hasMore={data.length < (count ?? 0)}
      loader={
        <div className="flex justify-center w-full py-4">
          <Loader size={16} />
        </div>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>
            {isLoading ? (
              <div className="!w-28 !h-[18px]">
                <SkeletonText />
              </div>
            ) : (
              <span>
                Pools{' '}
                <span className="text-gray-400 dark:text-slate-500">
                  ({totalCount ?? 0})
                </span>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <DataTable
          state={state}
          onSortingChange={setSorting}
          manualSorting
          loading={isLoading}
          linkFormatter={(row) => `/pool/v3/${row.address}`}
          rowRenderer={rowRenderer}
          columns={COLUMNS}
          data={data}
        />
      </Card>
    </InfiniteScroll>
  )
}
